import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AppShell from "../components/AppShell";
import ClientAvatar from "../components/ClientAvatar";
import CalendarWeekNav from "../components/CalendarWeekNav";
import PackageDetailsModal, {
  statusKeyFor,
  STATUS_META,
} from "../components/PackageDetailsModal";
import { formatRelativeTime } from "../components/notificationDisplay";
import { useMembers } from "../../../data/membersStore";
import { colorForName } from "../Members";
import "../Members/members.css";
import "../components/packageFlow.css";
import "./packageManagement.css";

// Same three buckets the trainer already sees per-client inside PackageDetailsModal, just
// rolled up across everyone at once — a package is "pending" regardless of payment (payment
// isn't decided yet while pending, per PackageStatusCard), everything else splits on
// `payment_date` alone.
const TABS = [
  { key: "pending", label: "Na čekanju", Icon: AccessTimeOutlinedIcon },
  { key: "paid", label: "Plaćeni", Icon: CheckCircleOutlineIcon },
  { key: "unpaid", label: "Neplaćeni", Icon: CancelOutlinedIcon },
];

// No global `moment.locale("sr")` here — see weekdayShort.js's own comment on why that's
// avoided (it would silently reformat every other moment() call in the app). Full nominative
// month names, for the "Mesec" nav's own label only.
const MONTHS_SR = [
  "Januar",
  "Februar",
  "Mart",
  "April",
  "Maj",
  "Jun",
  "Jul",
  "Avgust",
  "Septembar",
  "Oktobar",
  "Novembar",
  "Decembar",
];

// Shown next to "N termina" in each row, in place of price — a pending request has no
// start/end yet (view_clients_packages_full only derives those from real bookings), so it
// falls back to when the member actually requested it; an already-approved package with no
// bookings yet (a "Budući" cycle) has that exact same empty shape, just already decided.
const packageDateRangeText = (pkg, statusKey) => {
  const fmt = (d) => moment(d).format("D. MMM YYYY.");
  if (statusKey === "pending") {
    return pkg.created_at ? `Zahtevano ${fmt(pkg.created_at)}` : "Datum zahteva nepoznat";
  }
  if (pkg.start_date && pkg.end_date) return `${fmt(pkg.start_date)} - ${fmt(pkg.end_date)}`;
  if (pkg.start_date) return `Od ${fmt(pkg.start_date)}`;
  return "Odobren, još nije počeo";
};

const PackageManagement = () => {
  const { clients, packagesByClient, loading, error: loadError } = useMembers();
  const [search, setSearch] = useState("");
  // A single moment (always the 1st of some month) rather than a "YYYY-MM" string — lets
  // prev/next just add/subtract a month without reparsing. Defaults to the current month —
  // "koliko vežbača nije platilo ovog meseca" is the whole point of this filter, so that's
  // what a trainer should see the moment this screen opens.
  const [selectedMonth, setSelectedMonth] = useState(() => moment().startOf("month"));
  const monthLabel = `${MONTHS_SR[selectedMonth.month()]} ${selectedMonth.year()}.`;
  const [activeTab, setActiveTab] = useState("pending");
  const [reviewTarget, setReviewTarget] = useState(null);
  // Mobile-only floating "back to top" button (see packageManagement.css's
  // .hf-pm-scrolltop-fab) — the sticky search+tabs bar was removed from Mobile (it clashed
  // with the floating hamburger/bell header up there), so this is what replaces it as the way
  // back to the search/tabs after scrolling a few screens down the list. ~3 "scrolls" is
  // fuzzy by nature — 600px comfortably covers a few real scroll gestures without popping up
  // after just a nudge.
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // One row per package/cycle, not per client — a client with 2 paid packages shows up twice
  // under "Plaćeni". `statusKeyFor`/`STATUS_META` are the exact same helpers
  // PackageDetailsModal uses for its own badges, imported rather than re-derived here.
  //
  // "First App Package" — the free, 0-session legacy backfill placeholder every migrated
  // client got instead of a real purchased package (same one MemberPackageStatus.jsx already
  // relabels to "Nema aktivnih paketa" so it doesn't look like a real package there) — is
  // excluded here too: it has no price, so it can never really be "paid", and it was
  // dominating "Neplaćeni" (258 of 303 on a real count) with rows nobody actually needs to
  // chase payment on.
  const rows = useMemo(() => {
    const list = [];
    for (const client of clients) {
      const packages = packagesByClient[client.id] || [];
      for (const pkg of packages) {
        if (pkg.package_name === "First App Package") continue;
        list.push({ client, pkg, statusKey: statusKeyFor(pkg), isPaid: !!pkg.payment_date });
      }
    }
    return list;
  }, [clients, packagesByClient]);

  // By start_date (when the first real training actually happened/happens), not created_at
  // (when it was purchased/requested) — a package bought in August for a September start
  // belongs under "Septembar" here. Falls back to created_at only for a package with no
  // start_date at all yet (approved but nobody's booked into it — a "Budući" cycle, in
  // PackageDetailsModal's own terms) so it still lands under *some* month instead of silently
  // disappearing from every specific-month filter until it's actually started.
  const monthFilteredRows = useMemo(() => {
    const monthKey = selectedMonth.format("YYYY-MM");
    return rows.filter(
      (row) => moment(row.pkg.start_date || row.pkg.created_at).format("YYYY-MM") === monthKey
    );
  }, [rows, selectedMonth]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return monthFilteredRows;
    return monthFilteredRows.filter(({ client }) => {
      const haystack = `${client.first_name || ""} ${client.last_name || ""} ${
        client.email || ""
      } ${client.phone || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [monthFilteredRows, search]);

  const buckets = useMemo(() => {
    const byDate = (a, b) =>
      moment(b.pkg.created_at || b.pkg.start_date).diff(moment(a.pkg.created_at || a.pkg.start_date));
    const pending = [];
    const paid = [];
    const unpaid = [];
    for (const row of searchedRows) {
      if (row.statusKey === "pending") pending.push(row);
      else if (row.isPaid) paid.push(row);
      else unpaid.push(row);
    }
    pending.sort(byDate);
    paid.sort(byDate);
    unpaid.sort(byDate);
    return { pending, paid, unpaid };
  }, [searchedRows]);

  const activeRows = buckets[activeTab];

  const showSkeleton = loading && clients.length === 0;
  const showBlockingError = !loading && loadError && clients.length === 0;

  return (
    <AppShell active="packages">
      {/* No MobileHeader title here — the search bar below takes over that same fixed top
          band (between the hamburger and bell) on Mobile instead, so there's only ever one
          fixed thing up there, not two competing for the same space. */}
      <div className="hf-mem-page hf-pm-page">
        <div className="hf-mem-header">
          <div>
            <h1 className="hf-mem-title">Upravljanje paketima</h1>
            <p className="hf-mem-subtitle">
              Pregled svih kupljenih paketa i zahteva za odobravanje.
            </p>
          </div>
        </div>

        <div className="hf-pm-sticky-bar">
          <div className="hf-pm-search-row">
            <div className="hf-mem-search-wrap hf-pm-search-wrap">
              <SearchIcon fontSize="small" className="hf-mem-search-icon" />
              <input
                className="hf-mem-search-input"
                type="text"
                placeholder="Pretraži vežbača (ime, prezime, telefon)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Desktop/Tablet only, like the rest of .hf-mem-header/.hf-pm-stats this sits
                above — right-aligned so it reads as "belonging to" Neplaćeni, the rightmost
                card just below it. Filters every bucket (Na čekanju/Plaćeni/Neplaćeni) down to
                packages starting that month, so "Neplaćeni" reads as "koliko vežbača nije
                platilo ovog meseca" instead of an all-time count. Same prev/Today/next nav
                Kalendar and Termini already use (CalendarWeekNav), not a dropdown — "Today"
                here means "jump back to the current month". */}
            <div className="hf-pm-month-nav">
              {/* Always the "desktop" pill nav Kalendar/Termini use (not their own breakpoint
                  split) — same look on every screen size here, not just Tablet/Desktop.
                  `todayLabel` swaps its hardcoded "Danas" for the month name (see
                  CalendarWeekNav's own comment on that prop). scheduler.css hides
                  .hf-cal-nav-icon-btn/.hf-cal-today-btn below 768px by default (Calendar/Termini
                  only ever show this variant there) — packageManagement.css's own
                  .hf-pm-month-nav override below undoes that specifically for this page. */}
              <CalendarWeekNav
                variant="desktop"
                todayLabel={monthLabel}
                onPrev={() => setSelectedMonth((m) => m.clone().subtract(1, "month"))}
                onNext={() => setSelectedMonth((m) => m.clone().add(1, "month"))}
                onToday={() => setSelectedMonth(moment().startOf("month"))}
              />
            </div>
          </div>

          <div className="hf-pm-stats">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                className={`hf-pm-stat hf-pm-stat-${key}${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <span className="hf-pm-stat-icon">
                  <Icon fontSize="small" />
                </span>
                <span className="hf-pm-stat-text">
                  <span className="hf-pm-stat-label">{label}</span>
                  <span className="hf-pm-stat-value">{buckets[key].length}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {showBlockingError && (
          <div className="hf-mem-empty">
            <p>Nije moguće učitati listu paketa.</p>
          </div>
        )}

        {!showSkeleton && !showBlockingError && activeRows.length === 0 && (
          <div className="hf-mem-empty">
            <p>
              {search ? "Nema rezultata za pretragu." : "Nema paketa u ovoj kategoriji."}
            </p>
          </div>
        )}

        {!showBlockingError && (
          <div className="hf-pm-list">
            {activeRows.map(({ client, pkg, statusKey, isPaid }) => {
              const meta = STATUS_META[statusKey];
              const fullName =
                `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Bez imena";
              const initials =
                `${(client.first_name || "")[0] || ""}${(client.last_name || "")[0] || ""}`.toUpperCase();
              const qty = pkg.total_sessions ?? pkg.package_service_qty ?? 0;
              const dateSource = pkg.created_at || pkg.start_date;

              return (
                <div className="hf-pm-row" key={pkg.id}>
                  <div className="hf-pm-row-identity">
                    {/* Same breathing red-ring marker as the Scheduler's own unpaid-booking
                        avatars (scheduler.css's `.hf-appt-avatar-unpaid`, already loaded
                        app-wide since nothing here code-splits) — reused as-is, not redefined,
                        and works on Mobile too since that rule has no viewport-based override. */}
                    <ClientAvatar
                      photoUrl={client.photo_url}
                      initials={initials}
                      background={colorForName(fullName)}
                      wrapperClassName={`hf-mem-avatar hf-pm-avatar${
                        statusKey !== "pending" && !isPaid ? " hf-appt-avatar-unpaid" : ""
                      }`}
                      imgClassName="hf-mem-avatar-img"
                    />
                    <div className="hf-pm-row-name-wrap">
                      <div className="hf-pm-row-name">{fullName}</div>
                      <div className="hf-pm-row-pkg">
                        {qty} termina · {packageDateRangeText(pkg, statusKey)}
                      </div>
                    </div>
                  </div>

                  <div className="hf-pm-row-badges">
                    <span
                      className={`hf-pkg-status-badge ${
                        statusKey === "pending"
                          ? "hf-pm-badge-pending"
                          : `hf-pkg-status-badge-${meta.tone}`
                      }`}
                    >
                      {meta.Icon && <meta.Icon sx={{ fontSize: 13 }} />}
                      {meta.label}
                    </span>
                    {statusKey !== "pending" && (
                      <span
                        className={`hf-pkg-row-pay ${
                          isPaid ? "hf-pkg-row-pay-paid" : "hf-pkg-row-pay-unpaid"
                        }`}
                      >
                        {isPaid ? <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> : <CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                        {isPaid ? "Plaćeno" : "Nije plaćeno"}
                      </span>
                    )}
                  </div>

                  <div className="hf-pm-row-date">
                    {dateSource ? formatRelativeTime(dateSource) : ""}
                  </div>

                  <button
                    type="button"
                    className="hf-pm-row-action"
                    onClick={() => setReviewTarget({ client, pkg })}
                  >
                    {statusKey === "pending" ? "Pregledaj zahtev" : "Pregledaj paket"}
                    <ChevronRightIcon fontSize="small" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {reviewTarget && (
        <PackageDetailsModal
          open
          member={reviewTarget.client}
          packages={packagesByClient[reviewTarget.client.id] || []}
          onClose={() => setReviewTarget(null)}
          // Pending still lands on the summary view (its own review lives there); every other
          // row jumps straight to that one package's own detail instead of the full Budući/
          // Trenutni/Prošli list — this row already told us exactly which package they meant.
          initialCycleId={
            statusKeyFor(reviewTarget.pkg) === "pending" ? undefined : reviewTarget.pkg.id
          }
        />
      )}

      {showScrollTop && (
        <button
          type="button"
          className="hf-pm-scrolltop-fab"
          aria-label="Nazad na vrh"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <KeyboardArrowUpIcon />
        </button>
      )}
    </AppShell>
  );
};

export default PackageManagement;
